import React, { useEffect, useState, useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
  Text,
  Button,
  IconButton,
  Box,
  Flex,
  useBreakpointValue,
} from '@stockx/ui';
import { CloseIcon, ChevronDownIcon, CurrencyIcon } from '@stockx/icons';

import {
  Categories,
  Events as GoogleAnalyticsEvents,
  useGATrackEvent,
} from '@app/analytics';

import {
  LANGUAGES,
  useLanguageOptions,
  LanguageObject,
  LanguageKeys,
} from '@app/i18n';

import {
  getBrowserLanguageCode,
  getCountryCode,
} from '@app/redux/clientLocale/selectors';

import { useLocation } from '@app/router';
import { Region } from '@app/types';
import { ironBuildVersionIsV4 } from '@app/utils';

import {
  LocationSelectorDropdown,
  DropdownFormLabel,
} from './LocationSelector.styles';

import { useFormActions, useGetRegions } from '../../hooks';

const LocationSelector = () => {
  const { i18n } = useTranslation();
  const { isMobile, isTablet } = useBreakpointValue();
  const trackGAEvent = useGATrackEvent();
  const location = useLocation();
  const pathname = location?.pathname;

  const languageOptions: LanguageObject[] = useLanguageOptions();
  const browserLanguage: LanguageKeys = useSelector(getBrowserLanguageCode);
  const countryCode: Nullable<Region['code']> = useSelector(getCountryCode);

  const browserLocale =
    (ironBuildVersionIsV4
      ? LANGUAGES[browserLanguage]?.filePath
      : LANGUAGES[browserLanguage]?.value) || 'en';

  const browserLangTranslator = i18n.getFixedT(
    browserLocale,
    'feature.where-are-you',
  );

  const headerText = isMobile
    ? 'location_selector_header_mobile_web'
    : 'Update your location to improve your experience on StockX.';

  const translatedHeader = browserLangTranslator(headerText);

  const { dispatchSetShowWhereAreYouModal, saveSettings } = useFormActions();
  const { regions, regionOptions, regionsLoading, defaultRegion } =
    useGetRegions();

  const [displayDropdowns, setDisplayDropdowns] = useState(!isMobile);

  const [updatedRegion, setUpdatedRegion] = useState<Region | any>(
    defaultRegion,
  );

  const [updatedLanguage, setUpdatedLanguage] = useState<LanguageObject>(
    LANGUAGES[browserLanguage],
  );

  const [currentPage, setCurrentPage] = useState<Nullable<string>>(null);

  useEffectOnce(() => {
    trackGAEvent(
      GoogleAnalyticsEvents.LocationBannerDisplayed,
      Categories.LocationModal,
      `${countryCode} ip country`,
    );
  });

  //
  // Disable location selector from displaying on page transition
  useEffect(() => {
    if (pathname && currentPage === null) {
      setCurrentPage(pathname);
    }

    if (currentPage !== null && pathname !== currentPage) {
      dispatchSetShowWhereAreYouModal(false, false, true);
    }
  }, [pathname, currentPage, dispatchSetShowWhereAreYouModal]);

  //
  // The countries array takes a while to build, preventing updatedRegion
  // from being set properly by the default use state value
  useEffect(() => {
    if (!updatedRegion && defaultRegion?.value) {
      setUpdatedRegion(defaultRegion);
    }
  }, [defaultRegion, updatedRegion]);

  //
  // On mobile web only, clicking the container should display the dropdowns
  const handleContainerClick = useCallback(() => {
    if (!isMobile || displayDropdowns) {
      return;
    }

    setDisplayDropdowns(true);
  }, [isMobile, displayDropdowns]);

  return (
    <Box
      backgroundColor="green.700"
      px={isMobile ? 2 : 4}
      pb={3}
      pt={1}
      onClick={handleContainerClick}
    >
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mx={isMobile || isTablet ? 0 : 9}
        color="neutral.white"
        maxHeight={9}
        mb={isMobile ? 1 : 0}
        data-testid="location-selector-header"
      >
        <Flex direction="row" alignItems="center">
          {isMobile && <CurrencyIcon marginRight={1} boxSize={5} />}

          <Text fontSize={isMobile ? 'xs' : 'md'}>{translatedHeader}</Text>
        </Flex>

        <IconButton
          size="lg"
          data-testid="location-selector-close-button"
          aria-label={'Close Location Selector'}
          icon={<CloseIcon />}
          onClick={() => dispatchSetShowWhereAreYouModal(false, true, true)}
        />
      </Flex>

      {!displayDropdowns && (
        <Flex
          direction="row"
          ml={5}
          justifyContent="start"
          data-testid="location-selector-collapsed-view"
        >
          <Button size="sm">
            <Text fontSize="xs">
              {updatedRegion?.display} | {updatedLanguage?.display}
            </Text>
          </Button>
        </Flex>
      )}

      {displayDropdowns && (
        <Flex
          direction={isMobile ? 'column' : 'row'}
          ml={isMobile || isTablet ? 0 : 9}
          alignItems="center"
          data-testid="location-selector-dropdowns"
        >
          <Box
            borderColor="neutral.gray"
            borderWidth="1px"
            backgroundColor="neutral.white"
            width={isMobile ? '100%' : '341px'}
          >
            <DropdownFormLabel htmlFor="region-select">
              {browserLangTranslator('Region')}
            </DropdownFormLabel>
            <LocationSelectorDropdown
              id={'region-select'}
              icon={
                <ChevronDownIcon
                  position="absolute"
                  top="2px"
                  boxSize=".75em !important"
                />
              }
              value={updatedRegion?.value}
              onChange={(evt: React.ChangeEvent<HTMLSelectElement>) => {
                const region = regions.find(
                  ({ value }) => value === evt?.target?.value,
                );

                setUpdatedRegion(region);
              }}
            >
              {regionOptions?.map(({ display, value, key }) => (
                <option key={key} value={value}>
                  {display}
                </option>
              ))}
            </LocationSelectorDropdown>
          </Box>

          <Box
            borderColor="neutral.gray"
            borderWidth="1px"
            backgroundColor="neutral.white"
            mx={isMobile ? 0 : 5}
            my={isMobile ? 3 : 0}
            width={isMobile ? '100%' : '341px'}
          >
            <DropdownFormLabel htmlFor="language-select">
              {browserLangTranslator('Language')}
            </DropdownFormLabel>
            <LocationSelectorDropdown
              id={'language-select'}
              icon={
                <ChevronDownIcon
                  position="absolute"
                  top="2px"
                  boxSize=".75em !important"
                />
              }
              value={updatedLanguage?.value}
              onChange={(evt: React.ChangeEvent<HTMLSelectElement>) => {
                setUpdatedLanguage(LANGUAGES[evt?.target?.value]);
              }}
            >
              {languageOptions?.map(({ display, value }) => (
                <option key={display} value={value}>
                  {display}
                </option>
              ))}
            </LocationSelectorDropdown>
          </Box>

          <Box width={isMobile ? '100%' : 'auto'}>
            <Button
              variant="secondary-dark"
              size="lg"
              width={isMobile ? '100%' : 'auto'}
              onClick={() => saveSettings({ updatedRegion, updatedLanguage })}
              isLoading={regionsLoading}
              isDisabled={regionsLoading}
            >
              {browserLangTranslator('Update')}
            </Button>
          </Box>
        </Flex>
      )}
    </Box>
  );
};

export default LocationSelector;
